import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useSellerStore } from '@/store/seller';

export function DesktopMyStorePage() {
  const navigate = useNavigate();
  const { ToastContainer } = useToast();
  const { 
    salesMetrics, 
    businessStatus, 
    setSalesMetrics, 
    setBusinessStatus, 
    setLoading, 
    setError 
  } = useSellerStore();

  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const loadStoreData = async () => {
      try {
        setLoading(true);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }

        // 대시보드 데이터 가져오기
        const dashboardResult = await sellerService.getDashboardData(restaurantResult.restaurantId);
        if (dashboardResult.success && dashboardResult.data) {
          setSalesMetrics(dashboardResult.data.salesMetrics);
          setBusinessStatus(dashboardResult.data.businessStatus);
        }
        
      } catch (error) {
        console.error('Store data load error:', error);
        setError('매장 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
        setLoadingData(false);
      }
    };

    loadStoreData();
  }, [setLoading, setError, setSalesMetrics, setBusinessStatus]);

  if (loadingData) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">매장 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-[32px] font-bold text-gray-800 mb-4">내 가게</h1>
          
          {/* 영업 상태 표시 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-[24px] font-semibold text-gray-800">매장 현황</h2>
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 ${businessStatus ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-[20px] font-semibold">
                  {businessStatus ? '영업 중' : '영업 종료'}
                </span>
              </div>
            </div>
            
            {salesMetrics && (
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-[16px] font-medium text-blue-700 mb-2">{salesMetrics.currentMonth} 매출</h3>
                  <p className="text-[24px] font-bold text-blue-800">{salesMetrics.monthlyRevenue.toLocaleString()}원</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-[16px] font-medium text-green-700 mb-2">판매건수</h3>
                  <p className="text-[24px] font-bold text-green-800">{salesMetrics.monthlySalesCount}건</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 판매 관리 섹션 */}
        <div className="mb-10">
          <h2 className="text-[24px] font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-200">판매 관리</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">판매내역</h3>
                  <p className="text-[16px] text-gray-600 mb-4">매출 현황과 판매 내역을 확인하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store/sales-history')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  상세보기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">오늘의 마감할인</h3>
                  <p className="text-[16px] text-gray-600 mb-4">당일 마감할인 상품을 설정하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store/deadline-discount')}
                  className="w-full py-3 bg-green-500 text-white text-[16px] font-semibold rounded-lg hover:bg-green-600 transition-colors"
                >
                  설정하기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">예약할인 상품</h3>
                  <p className="text-[16px] text-gray-600 mb-4">예약할인 상품을 설정하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store/reservation-discount')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  설정하기
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 상품 관리 섹션 */}
        <div className="mb-10">
          <h2 className="text-[24px] font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-200">상품 관리</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">상품 등록</h3>
                  <p className="text-[16px] text-gray-600 mb-4">새로운 상품을 등록하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store/product-register')}
                  className="w-full py-3 bg-green-500 text-white text-[16px] font-semibold rounded-lg hover:bg-green-600 transition-colors"
                >
                  등록하기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">상품 수정</h3>
                  <p className="text-[16px] text-gray-600 mb-4">등록된 상품을 수정하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store/product-edit')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  수정하기
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 가게 관리 섹션 */}
        <div className="mb-10">
          <h2 className="text-[24px] font-bold text-gray-800 mb-6 pb-3 border-b-2 border-gray-200">가게 관리</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">가게 정보 수정</h3>
                  <p className="text-[16px] text-gray-600 mb-4">가게 기본 정보를 수정하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store-edit')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  수정하기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">영업시간 설정</h3>
                  <p className="text-[16px] text-gray-600 mb-4">요일별 영업시간을 설정하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/operating-hours')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  설정하기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">리뷰 보기</h3>
                  <p className="text-[16px] text-gray-600 mb-4">고객 리뷰를 확인하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store-reviews')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  보기
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h3 className="text-[20px] font-bold text-gray-800 mb-3">내 가게 모니터링</h3>
                  <p className="text-[16px] text-gray-600 mb-4">소비자 관점에서 내 가게를 확인하세요</p>
                </div>
                <button
                  onClick={() => navigate('/seller/store-monitoring')}
                  className="w-full py-3 bg-gray-100 text-gray-700 text-[16px] font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                >
                  모니터링
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}